import React, { useState } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { importChocolateRecipes } from '../services/externalRecipeService';
import RecipeImage from '../components/RecipeImage';

const ImportRecipesPage = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [importedRecipes, setImportedRecipes] = useState([]);
  const [error, setError] = useState(null);
  const [importCount, setImportCount] = useState(5);
  const navigate = useNavigate();

  const handleImport = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      // Importer les recettes à base de chocolat
      const recipes = await importChocolateRecipes(importCount);
      
      setImportedRecipes(recipes);
      setIsLoading(false);
    } catch (err) {
      setError('Une erreur est survenue lors de l\'importation des recettes.');
      setIsLoading(false);
      console.error(err);
    }
  };

  return (
    <PageContainer>
      <Header>
        <h1>Importer des Recettes</h1>
        <p>Enrichissez votre collection de recettes avec des délicieuses créations à base de chocolat adaptées pour Tartina.</p>
      </Header>

      <ImportControls>
        <ImportDescription>
          <h2>Importer depuis TheMealDB</h2>
          <p>Cette fonctionnalité vous permet d'importer automatiquement des recettes à base de chocolat depuis TheMealDB, en les adaptant pour utiliser la pâte à tartiner Tartina.</p>
        </ImportDescription>

        <ImportForm>
          <FormGroup>
            <label htmlFor="importCount">Nombre de recettes à importer:</label>
            <CountInput 
              type="number" 
              id="importCount" 
              value={importCount} 
              onChange={(e) => setImportCount(Math.max(1, Math.min(10, parseInt(e.target.value) || 1)))} 
              min="1" 
              max="10"
            />
          </FormGroup>

          <ActionButtons>
            <ImportButton onClick={handleImport} disabled={isLoading}>
              {isLoading ? 'Importation en cours...' : 'Importer des recettes'}
            </ImportButton>
            <CancelButton onClick={() => navigate('/recipes')}>
              Retour aux recettes
            </CancelButton>
          </ActionButtons>
        </ImportForm>
      </ImportControls>

      {error && (
        <ErrorMessage>
          {error}
        </ErrorMessage>
      )}

      {importedRecipes.length > 0 && (
        <ResultsSection>
          <h2>Recettes importées avec succès!</h2>
          <p>{importedRecipes.length} recettes ont été ajoutées à votre collection.</p>
          
          <RecipeGrid>
            {importedRecipes.map((recipe) => (
              <RecipeCard key={recipe.id}>
                <RecipeImage src={recipe.image_url} alt={recipe.name} />
                <RecipeContent>
                  <RecipeTitle>{recipe.name}</RecipeTitle>
                  <RecipeDescription>
                    {recipe.description ? recipe.description.substring(0, 80) + '...' : 'Une délicieuse recette avec Tartina.'}
                  </RecipeDescription>
                  <RecipeMeta>
                    <RecipeCost>{recipe.estimated_cost} FCFA</RecipeCost>
                    <RecipeRating>&#9733; {recipe.rating}/5</RecipeRating>
                  </RecipeMeta>
                  <ViewButton onClick={() => navigate(`/recipes/${recipe.id}`)}>Voir la recette</ViewButton>
                </RecipeContent>
              </RecipeCard>
            ))}
          </RecipeGrid>
          
          <ViewAllButton onClick={() => navigate('/recipes')}>
            Voir toutes les recettes
          </ViewAllButton>
        </ResultsSection>
      )}
    </PageContainer>
  );
};

// Styles
const PageContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: ${({ theme }) => theme.spacing.xl};
`;

const Header = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing.xl};
  
  h1 {
    margin-bottom: ${({ theme }) => theme.spacing.sm};
  }
  
  p {
    color: ${({ theme }) => theme.colors.textSecondary};
    font-size: 1.1rem;
  }
`;

const ImportControls = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.xl};
  margin-bottom: ${({ theme }) => theme.spacing.xl};
  background-color: ${({ theme }) => theme.colors.bgLight};
  border-radius: 8px;
  padding: ${({ theme }) => theme.spacing.lg};
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing.md};
  }
`;

const ImportDescription = styled.div`
  flex: 1;
  
  h2 {
    margin-bottom: ${({ theme }) => theme.spacing.sm};
  }
  
  p {
    color: ${({ theme }) => theme.colors.textSecondary};
  }
`;

const ImportForm = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const FormGroup = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing.md};
  
  label {
    display: block;
    margin-bottom: ${({ theme }) => theme.spacing.xs};
    font-weight: 500;
  }
`;

const CountInput = styled.input`
  width: 100%;
  padding: ${({ theme }) => theme.spacing.sm};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 4px;
  font-size: 1rem;
  
  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

const ActionButtons = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.md};
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    flex-direction: column;
  }
`;

const ImportButton = styled.button`
  background-color: ${({ theme }) => theme.colors.primary};
  color: white;
  border: none;
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.lg};
  border-radius: 4px;
  font-weight: 600;
  cursor: pointer;
  transition: ${({ theme }) => theme.transitions.fast};
  
  &:hover {
    background-color: ${({ theme }) => theme.colors.primaryDark};
  }
  
  &:disabled {
    background-color: ${({ theme }) => theme.colors.disabled};
    cursor: not-allowed;
  }
`;

const CancelButton = styled.button`
  background-color: transparent;
  color: ${({ theme }) => theme.colors.textPrimary};
  border: 1px solid ${({ theme }) => theme.colors.border};
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.lg};
  border-radius: 4px;
  font-weight: 600;
  cursor: pointer;
  transition: ${({ theme }) => theme.transitions.fast};
  
  &:hover {
    background-color: ${({ theme }) => theme.colors.bgDark};
  }
`;

const ErrorMessage = styled.div`
  background-color: ${({ theme }) => theme.colors.error};
  color: white;
  padding: ${({ theme }) => theme.spacing.md};
  border-radius: 4px;
  margin-bottom: ${({ theme }) => theme.spacing.lg};
`;

const ResultsSection = styled.div`
  margin-top: ${({ theme }) => theme.spacing.xl};
  
  h2 {
    margin-bottom: ${({ theme }) => theme.spacing.md};
  }
  
  p {
    margin-bottom: ${({ theme }) => theme.spacing.lg};
    color: ${({ theme }) => theme.colors.textSecondary};
  }
`;

const RecipeGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: ${({ theme }) => theme.spacing.lg};
  margin-bottom: ${({ theme }) => theme.spacing.xl};
`;

const RecipeCard = styled.div`
  background: ${({ theme }) => theme.colors.cardBg};
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  overflow: hidden;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.08);
  transition: ${({ theme }) => theme.transitions.fast};
  display: flex;
  flex-direction: column;
  height: 100%;
  
  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 8px 15px rgba(0, 0, 0, 0.12);
  }
`;

const RecipeContent = styled.div`
  padding: ${({ theme }) => theme.spacing.md};
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  flex: 1;
`;

const RecipeTitle = styled.h3`
  margin: 0;
  font-size: 1.1rem;
`;

const RecipeDescription = styled.p`
  margin-bottom: ${({ theme }) => theme.spacing.md};
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: 0.9rem;
`;

const RecipeMeta = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

const RecipeCost = styled.span`
  font-weight: 600;
`;

const RecipeRating = styled.span`
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const ViewButton = styled.button`
  width: 100%;
  padding: ${({ theme }) => theme.spacing.sm};
  background-color: ${({ theme }) => theme.colors.primary};
  color: ${({ theme }) => theme.colors.light};
  border: none;
  border-radius: ${({ theme }) => theme.borderRadius.small};
  font-weight: 600;
  cursor: pointer;
  transition: ${({ theme }) => theme.transitions.fast};
  
  &:hover {
    background-color: ${({ theme }) => theme.colors.tertiary};
  }
`;

const ViewAllButton = styled.button`
  display: block;
  margin: 0 auto;
  padding: ${({ theme }) => theme.spacing.md} ${({ theme }) => theme.spacing.xl};
  background-color: ${({ theme }) => theme.colors.primary};
  color: white;
  border: none;
  border-radius: 4px;
  font-weight: 600;
  cursor: pointer;
  transition: ${({ theme }) => theme.transitions.fast};
  
  &:hover {
    background-color: ${({ theme }) => theme.colors.primaryDark};
  }
`;

export default ImportRecipesPage;
